import { PricingSimulatorStatusType, PricingSimulatorUploadStatusType } from 'utils/Enums';

export type Scenario = {
    scenarioDetailsLoading?: boolean;
    scenarioName: string;
    scenarioId: number;
    projectId: number;
    activeLense?: ActiveLenseConfig; // the lense data which is currently open
    createdBy: string;
    updatedAt: string;
    status: PricingSimulatorStatusType;
    lenseConfig?: Array<LenseConfig>;
    lenseConfigBackup?: Array<LenseConfig>;
    dimAttributes?: AttributeTypeItem[];
    // limit: 50; // read it from config
    offset?: number;
    count?: number;
    sortObject?: SortType;
    data?: any;
    error?: any;
    search?: string;
    isPrivate?: boolean;
    bulkUploadInfo?: BulkUploadInfo;
};

export type BulkUploadInfo = {
    status?: PricingSimulatorUploadStatusType;
    errorObject?: any;
};

export type AttributeTypeItem = {
    label: string;
    value: string;
    dimension: string;
};

export type SortType = {
    sortBy: string;
    sortingOrder: 'asc' | 'desc';
    sortType: 'new' | 'actual' | 'percentage_change' | 'incremental_change';
};

export type Project = {
    projectName: string;
    projectId: number;
    createdBy: string;
    lastUpdated: string;
    dataSource: string;
    dim1Filter: any;
    dim2Filter: any;
    status: PricingSimulatorStatusType;
    simulationPeriod: string;
    priceEffectiveDate: string;
    sortObject?: SortType;
    simulationList?: Array<Scenario>; // this will be loaded only in scenario window
    isPrivate?: boolean;
};

export type LenseConfig = {
    isAllSelected?: boolean;
    isSelected?: boolean;
    lenseName: string;
    lenseId: number;
    attributes: Array<LenseAttribute>;
    isRecentlyUsed?: boolean;
};

export type ActiveLenseConfig = {
    activeLenseId?: number;
    activeLenseData?: LenseConfig;
};

export enum KpiTypeId {
    INPUT = 1,
    OUTPUT = 2,
    TIME_EFFECTIVE = 3,
    DISPLAY_ONLY = 5, // this will have just one column in the table
}

export type LenseAttribute = {
    isSelected: boolean;
    name: string;
    attributeId: number;
    type?: 'Cell' | 'Checkbox' | 'Label'; // TODO - remove it later, will not be required
    isEditable?: boolean; // if true its input column else output column
    pre?: string;
    post?: string;
    kpiTypeId?: KpiTypeId;
    availableForBulkUpload?: boolean;
    kpiKeyName?: string;
    allowIncrementChange?: boolean;
    allowAbsChange?: boolean;
    allowFinalValueChange?: boolean;
    allowedDecimals?: number;
    hasKpiRulesValidations?: boolean;
    allowNegative?: boolean;
};

export type EditableFieldsType = 'cell' | 'selected' | 'viewable' | 'all';
export type ResetFieldsType = 'filtered' | 'selected' | 'all';

export type tabsType = {
    key: string;
    label: string;
    url?: string;
    headerLabel?: string;
    component?: React.ReactNode;
    disabled?: boolean;
};

export type PricingReport = {
    reportId: number;
    reportName: string;
    createdBy: string;
    updatedAt: string;
    baseScenario: ReportScenario;
    reportFilters: any;
    baseKPIs: BaseKpis;
    changeAnalytics?: ChangeAnalytics;
    categoryMovement?: CategoryMovement;
    regionAnalytics?: RegionAnalytics;
    isPrivate?: boolean;
};

export type BaseKpis = {
    selectedKPIs: number[]; // selected kpis
    kpiConfig?: LenseConfig[]; // to manage state
    kpiTiles?: KPITileItem[]; // to store the output data for each of the kpis
    initialGlobalKpiIds?: number[]; // to store the initial global kpi ids
};

export type ReportScenario = {
    scenarioName: string;
    scenarioId: number;
    status: PricingSimulatorStatusType;
    isPrivate: boolean;
};

export type ChangeAnalytics = {
    input?: ChangeAnalyticsInputType[];
    output?: ChangeAnalyticsOutputType[];
};

export type ChangeAnalyticsInputType = {
    lensConfig?: LenseConfig[];
    graphId?: number;
    attributeType?: AttributeTypeItem;
    kpiId?: number;
};

export type ChangeAnalyticsOutputType = {
    data: any[];
    minVal?: number;
    maxVal?: number;
    id?: number;
    count: number;
    width?: number;
};

export type RegionAnalytics = {
    level: string;
    charts: any[];
};

export type CategoryMovement = {
    input?: CategoryMovementInputChartItem[];
    output?: {
        topChartItems?: CategoryMovementOutputChartItem[];
        bottomChartItems?: CategoryMovementOutputChartItem[];
    };
};

export type CategoryMovementInputChartItem = {
    bottomChartAttributeType?: AttributeTypeItem;
    bottomChartKpiId?: string;
};

export type CategoryMovementOutputChartItem = {
    data: any[];
    minVal?: number;
    maxVal?: number;
    id?: string;
    count?: number;
    width?: number;
    labelObject?: {
        mainLabel: string;
        yAxis: string;
        xAxis: string;
    };
};

export type KPITileItem = {
    label: string;
    id: number;
    newValue: number;
    oldValue: number;
    absChange: number;
    percentChange: number;
};

export type ScenarioComparison = {
    selectedKpiItem?: { id: number; label: string; pre: string; post: string };
    tableState?: ComparisonTableState;
    selectedScenarios: Array<ReportScenario>; // keep the base scenario at first index
};

export type ComparisonTableState = {
    dimAttributes?: AttributeTypeItem[];
    offset?: number;
    count?: number;
    search?: string;
    sortObject?: SortType;
    data?: any[];
};

export type DimensionDropdownType = {
    label: string;
    value: string;
    dimension: string;
};

type DimensionDropdownTypeAlt = {
    label: string;
    key: string;
    dimension: string;
    orderByName: boolean;
};

type ScenarioFilterDimensionInfo = {
    [dimension: string]: {
        label: string;
        key: string;
    };
};

export type DimensionConfigType = {
    dim1DropdownList: null | DimensionDropdownType[];
    defaultDim1DropdownItem: null | DimensionDropdownType;
    dim2DropdownList: null | DimensionDropdownType[];
    defaultDim2DropdownItem: null | DimensionDropdownType;
    scenarioFilterKeys: null | DimensionDropdownType[];
    dim1Attribute: null | DimensionDropdownTypeAlt;
    dim1NestedAttribute: null | DimensionDropdownTypeAlt;
    dim2Attribute: null | DimensionDropdownTypeAlt;
    lowestDimLensNames: null | string[];
    scenarioFilterDimensionInfo: ScenarioFilterDimensionInfo;
};

export enum EditedRecordOptions {
    YES = 'yes',
    NO = 'no',
    ALL = 'all',
}
